import { useEffect, useRef } from 'react'

import { keyWithoutModifier } from '@sketch/utils'

/**
 * Fires `onTrigger` when `key` is pressed without a modifier and no text input
 * is focused (handled by `keyWithoutModifier`).
 *
 * The listener is meant to live in the same component as an action's click
 * handler, so a shortcut is only active while its button is actually rendered —
 * which mirrors how the preview only mounts the actions relevant to the current
 * notification. The handler is read through a ref so the listener attaches once
 * per key instead of re-subscribing on every render.
 */
export const useInboxActionShortcut = (key: string, onTrigger: () => void) => {
  const handlerRef = useRef(onTrigger)
  handlerRef.current = onTrigger

  useEffect(() => {
    const matches = keyWithoutModifier(key)
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore OS auto-repeat from a held key — otherwise a mutation that
      // resolves mid-hold lets the next repeat fire a fresh one (duplicate
      // downloads, pin/unpin ping-pong, stacked history pushes).
      if (event.repeat) return
      if (!matches(event)) return
      event.preventDefault()
      handlerRef.current()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [key])
}
